import { Component, computed, DestroyRef, ElementRef, inject, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { isInIframe } from '@helpers/utils/is-in-iframe.util';
import { isDarkLuma } from '@helpers/utils/color.util';
import { APP_CONFIG } from '@configuration/configuration';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { NavigationComponent } from './navigation/navigation.component';
import { BrandComponent } from './brand/brand.component';
import { BackButtonComponent } from './navigation/back-button/back-button.component';
import { MenuComponent } from './menu/menu.component';
import { NeighborsComponent } from './neighbors/neighbors.component';
import { LegendComponent } from './legend/legend.component';
import { FullscreenToggleComponent } from "./fullscreen-toggle/fullscreen-toggle.component";
import { HyperlinkComponent } from './hyperlink/hyperlink.component';
import { DvmService } from '@services/dvm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import anime from 'animejs';
import { BenefitsComponent } from './benefits/benefits.component';
import { LayoutService } from '@services/layout.service';
import { ActionButtonsComponent } from "../footer/action-buttons/action-buttons.component";
import { SearchComponent } from './navigation/search/search.component';
import { MobileButtonComponent } from './navigation/mobile-button/mobile-button.component';
import { SearchService } from '@services/components/search.service';
import { MobileButtonService } from '@services/components/mobile-button.service';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';
import { ThemeMode } from '@models/enums/App/ThemeMode.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';


@Component({
    selector: 'app-topbar',
    imports: [
        NavigationComponent,
        BrandComponent,
        MenuComponent,
        NeighborsComponent,
        BenefitsComponent,
        LegendComponent,
        HyperlinkComponent,
        FullscreenToggleComponent,
        ActionButtonsComponent,
        SearchComponent,
        MobileButtonComponent,
        BackButtonComponent
    ],
    templateUrl: './topbar.component.html',
    styleUrl: './topbar.component.css'
})
export class TopbarComponent implements OnInit {

  // VIEWCHILDS
  @ViewChild('topbarContainer', {static: true}) 
  private readonly topbarContainer!: ElementRef<HTMLElement>

  // SERVICES
  private readonly dvmService:       DvmService      = inject(DvmService);
  private readonly destroyRef:       DestroyRef      = inject(DestroyRef);
  private readonly routeParams:      ViewerRouteParamsService = inject(ViewerRouteParamsService);

  // COMPONENT STATE
  private readonly searchService:    SearchService           = inject(SearchService);
  public  readonly isMobile:         Signal<boolean>         = inject(LayoutService).isMobile;
  // Search ocupa espacio mientras está activo o animando su salida -> ocultamos benefits/menu hasta que colapsa
  public  readonly searchOpen:       Signal<boolean>         = computed((): boolean => !!this.searchService.searchActive() || this.searchService.searchVisible());
  private readonly mobileButtonService: MobileButtonService  = inject(MobileButtonService);
  protected readonly mobileButtonActive: Signal<boolean>     = this.mobileButtonService.active;
  protected readonly hyperlinkMode:  boolean                 = this.routeParams.hasViewId;
  protected readonly iframeMode:     boolean                 = isInIframe();
  // Top level = Topview/Main (or view not yet determined); false on Seatmap/Pano.
  protected readonly isTopLevelView: Signal<boolean>         = computed((): boolean => {
    const view: AppViews | null = this.dvmService.view();
    return view !== AppViews.Seatmap && view !== AppViews.Pano;
  });
  // En fullscreen la barra pierde el gradiente (fondo transparente) y solo queda el toggle.
  protected readonly isFullscreen:   WritableSignal<boolean> = signal(false);

  // MODE — non-iframe derives from gradient colors (left=main, right=secondary).
  // Iframe ignores the colors: landing views follow the configured theme mode,
  // seatmaps/panos force dark styling.
  private readonly config: Signal<Configuration> = inject(APP_CONFIG);
  protected readonly leftMode:  Signal<'on-dark' | 'on-light'> = computed(() => this.modeFor(this.config().General.theme.color_main));
  protected readonly rightMode: Signal<'on-dark' | 'on-light'> = computed(() => this.modeFor(this.config().General.theme.color_secondary));

  private modeFor(hex: string): 'on-dark' | 'on-light' {
    // En fullscreen no hay barra detrás: el toggle descansa directamente sobre
    // el pano, así que siempre usa la variante clara.
    if (this.isFullscreen()) return 'on-dark';
    if (this.iframeMode) {
      // Seatmaps/panos are full of colorful map content: white text is the
      // only thing that reliably reads on top, regardless of the theme.
      if (!this.isTopLevelView()) return 'on-dark';
      return this.config().General.theme.mode === ThemeMode.Dark ? 'on-dark' : 'on-light';
    }
    return isDarkLuma(hex) ? 'on-dark' : 'on-light';
  }

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
  };

  // METHODS
  private initComponent(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.initSuscriptions());

  }

  private initSuscriptions(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.getObservable('fullscreen_enabled').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void  => {
      this.isFullscreen.set(true);
      this.toggleVisibility(false);
    });

    viewer3d.getObservable('fullscreen_disabled').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this.isFullscreen.set(false);
      this.toggleVisibility(true);
    });

  }


  private toggleVisibility(show: boolean): void {
    if (this.hyperlinkMode) return;
    
    anime({
      targets: this.getVisibilityElements(),
      opacity: show ? 1 : 0,
      easing: 'easeInOutQuad',
      duration: 300,
    })

  }

  private getVisibilityElements(): HTMLElement[] {

    // Celdas del navbar: [izquierda, derecha] en modo normal; en iframe hay una celda
    // extra de neighbors entre medias: [izquierda, neighbors, derecha].
    const navbarChildrenElements:   HTMLElement[] = Array.from(this.topbarContainer.nativeElement.children) as HTMLElement[],
        // Selector del componente de fullscreen toggle
        toggleFullscreenSelector:   string        = 'app-fullscreen-toggle',
        // Índice de la celda derecha según el modo
        rightCellIndex:             number        = this.iframeMode ? 2 : 1,
        // Celda derecha: puede no existir (en mobile+iframe la barra principal no se
        // renderiza en seatmap/pano y solo queda la sub-nav, que contiene el toggle).
        rightCell:                  HTMLElement | undefined = navbarChildrenElements[rightCellIndex];

    if (!rightCell) return [];

    // Celdas que se ocultan enteras (izquierda + neighbors en iframe)
    const fixedElements:            HTMLElement[] = navbarChildrenElements.slice(0, rightCellIndex),
        // Filtramos los children de la celda derecha exluyendo el toggle fullscreen (debe
        // seguir visible para poder salir) y cogemos el primer child (no el host element)
        dynamicElements:            HTMLElement[] = Array.from(rightCell.children).filter((element: Element)=> element.tagName.toLowerCase() !== toggleFullscreenSelector).map(el => el.firstChild as HTMLElement) as HTMLElement[];

    // Retornamos fixed elements y dynamic elements filtrados:
    return [...fixedElements, ...dynamicElements];

  }

}
